import React, { useState } from 'react';
import {
  getSubtotal,
  getCouponDiscount,
  isFreeShippingCoupon,
  hasCouponApplied,
  calculateCartTotal,
} from '../utils/cartCalculations';

const OrderSummary = ({ cart, config, shippingMethods, selectedShippingMethod, loading, appliedTip, onCartUpdate }) => {
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [isExpanded, setIsExpanded] = useState(false); // Mobile collapse state
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [discountSuccess, setDiscountSuccess] = useState('');

  // Get API base URL
  const apiBaseUrl = process.env.REACT_APP_API_URL || '';

  // Apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || discountLoading) return;

    setDiscountLoading(true);
    setDiscountError('');
    setDiscountSuccess('');

    try {
      const response = await fetch(`${apiBaseUrl}/cart/${cart.cart_id}/apply-discount/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: discountCode.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        // Don't show success message - the discount tag appearing is sufficient feedback
        setDiscountCode('');
        // Refresh cart data to show the applied discount tag
        if (onCartUpdate) {
          onCartUpdate();
        }
      } else {
        setDiscountError(data.message || 'Failed to apply discount code');
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('Failed to apply discount code. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // Remove discount code
  const handleRemoveDiscount = async () => {
    if (discountLoading) return;

    setDiscountLoading(true);
    setDiscountError('');
    setDiscountSuccess('');

    try {
      const response = await fetch(`${apiBaseUrl}/cart/${cart.cart_id}/remove-discount/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setDiscountSuccess('Discount removed');
        // Refresh cart data
        if (onCartUpdate) {
          onCartUpdate();
        }
      } else {
        setDiscountError(data.message || 'Failed to remove discount');
      }
    } catch (error) {
      console.error('Error removing discount:', error);
      setDiscountError('Failed to remove discount. Please try again.');
    } finally {
      setDiscountLoading(false);
    }
  };

  // Handle Enter key in discount input
  const handleDiscountKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyDiscount();
    }
  };

  // Show loading skeleton while cart is loading
  if (loading || !cart) {
    return (
      <div className="order-summary">
        <div style={{
          padding: '20px',
          textAlign: 'center',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div className="spinner-small" style={{ margin: '0 auto 12px' }}></div>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            Loading order summary...
          </p>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) return null;

  // Use centralized calculation functions from utils/cartCalculations.js
  const subtotal = getSubtotal(cart);
  const codeDiscount = getCouponDiscount(cart);
  const isFreeShippingCode = isFreeShippingCoupon(cart);
  const hasCodeDiscount = hasCouponApplied(cart);

  // Calculate base shipping cost from selected method
  let originalShipping = 0;
  if (selectedShippingMethod && shippingMethods && shippingMethods.length > 0) {
    const method = shippingMethods.find(m => m.handle === selectedShippingMethod || (m.id && m.id.toString() === selectedShippingMethod));
    if (method) {
      originalShipping = parseFloat(method.price);
    }
  }

  // Final shipping (0 if free shipping coupon)
  const shipping = isFreeShippingCode ? 0 : originalShipping;

  // Use centralized total calculation
  const total = calculateCartTotal({
    cart,
    shippingCost: originalShipping,
    tip: appliedTip,
  });

  // Calculate shipping savings from free shipping code
  const shippingSavings = isFreeShippingCode ? originalShipping : 0;
  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="order-summary">
      {/* Mobile Toggle Header */}
      <div className="order-summary-mobile-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="mobile-header-left">
          <span className="mobile-toggle-icon">{isExpanded ? '▲' : '▼'}</span>
          <span className="mobile-header-text">
            {isExpanded ? 'Hide order summary' : 'Show order summary'}
          </span>
        </div>
        <div className="mobile-header-total">
          ${total.toFixed(2)}
        </div>
      </div>

      {/* Collapsible Content */}
      <div className={`order-summary-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {/* Product Items */}
        {cart.items.map((item) => {
        const productImage = item.product.images && item.product.images.length > 0
          ? item.product.images[0]
          : 'https://via.placeholder.com/64x64?text=Product';

        return (
          <div key={item.id} className="product-item">
            <div className="product-image-wrapper">
              <img
                src={productImage}
                alt={item.product.name}
                className="product-thumbnail"
              />
              <span className="product-quantity-badge">{item.quantity}</span>
            </div>

            <div className="product-details">
              <h3>{item.product.name}</h3>
              {item.variant_title && item.variant_title !== 'Default Title' && (
                <div className="variant-info">
                  {item.variant_title}
                </div>
              )}
              {/* Quantity Discount Badge */}
              {item.has_discount && item.savings && (
                <div className="discount-badge">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" focusable="false" aria-hidden="true" style={{ marginRight: '6px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 3.25v2.844a2.5 2.5 0 0 1-.708 1.743L7.75 12.25m1-10.5H6.699a2 2 0 0 0-1.414.586L1.737 5.883a1.75 1.75 0 0 0 0 2.475l2.332 2.331a1.5 1.5 0 0 0 2.121 0l3.724-3.724a2 2 0 0 0 .586-1.414V3.5a1.75 1.75 0 0 0-1.75-1.75" fill="none" stroke="currentColor"/>
                    <circle cx="7.75" cy="4.5" r="0.563" strokeLinecap="round" strokeLinejoin="round" fill="none" stroke="currentColor"/>
                    <path strokeLinejoin="round" d="M7.74 4.49h.02v.02h-.02z" fill="none" stroke="currentColor"/>
                  </svg>
                  VOLUME DISCOUNT (-${parseFloat(item.savings * item.quantity).toFixed(2)})
                </div>
              )}
            </div>

            <div className="product-item-price">
              {/* Show strikethrough original price if discounted */}
              {item.has_discount && item.original_price && (
                <div className="original-price">
                  ${parseFloat(item.original_price).toFixed(2)}
                </div>
              )}
              <div className={item.has_discount ? 'discounted-price' : ''}>
                ${parseFloat(item.total).toFixed(2)}
              </div>
            </div>
          </div>
        );
      })}

      {/* Discount Section */}
      <div className="discount-section">
        {/* Discount code input - always enabled */}
        <div className="discount-input-group">
          <input
            type="text"
            placeholder="Discount code"
            value={discountCode}
            onChange={(e) => {
              setDiscountCode(e.target.value);
              setDiscountError('');
              setDiscountSuccess('');
            }}
            onKeyPress={handleDiscountKeyPress}
            disabled={discountLoading}
          />
          <button
            type="button"
            className="discount-apply-btn"
            onClick={handleApplyDiscount}
            disabled={!discountCode.trim() || discountLoading}
          >
            {discountLoading ? <span className="spinner-small spinner-dark"></span> : 'Apply'}
          </button>
        </div>

        {/* Applied discount tag - Shopify style */}
        {hasCodeDiscount && (
          <div className="applied-discount-tag">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" focusable="false" aria-hidden="true">
              <g strokeLinejoin="round" clipPath="url(#discount_svg__a)">
                <path strokeLinecap="round" d="M10.5 1.5H7.93a2.5 2.5 0 0 0-1.8.766L1.52 7.052a1.5 1.5 0 0 0 .02 2.101l3.48 3.48a1.25 1.25 0 0 0 1.75.016l5.116-4.926a2 2 0 0 0 .613-1.441V3.5a2 2 0 0 0-2-2" fill="none" stroke="currentColor"/>
                <circle cx="9.5" cy="4.5" r="0.563" strokeLinecap="round" fill="none" stroke="currentColor"/>
                <path d="M9.49 4.49h.02v.02h-.02z" fill="none" stroke="currentColor"/>
              </g>
              <defs>
                <clipPath id="discount_svg__a">
                  <path fill="#fff" d="M0 0h14v14H0z"/>
                </clipPath>
              </defs>
            </svg>
            <span className="discount-code-text">{cart.discount_code}</span>
            <button
              type="button"
              className="remove-discount-btn"
              onClick={handleRemoveDiscount}
              disabled={discountLoading}
              aria-label="Remove discount"
            >
              {discountLoading ? <span className="spinner-small spinner-dark spinner-xs"></span> : '×'}
            </button>
          </div>
        )}

        {/* Error message - Banner style with dismiss button */}
        {discountError && (
          <div className="discount-error-inline">
            <span className="discount-error-text">
              <strong>{discountCode}</strong> {discountError}
            </span>
            <button
              className="discount-error-dismiss"
              onClick={() => setDiscountError('')}
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        )}

        {/* Success message - only show briefly */}
        {discountSuccess && !hasCodeDiscount && (
          <div className="discount-success-inline">
            {discountSuccess}
          </div>
        )}
      </div>

      {/* Price Summary */}
      <div className="price-summary">
        <div className="price-row subtotal">
          <span>Subtotal</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>

        <div className="price-row shipping">
          <span>
            Shipping
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
              <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1"/>
              <path d="M7 4.5v3M7 9.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
          <span>
            {!selectedShippingMethod && originalShipping === 0
              ? 'Enter shipping address'
              : isFreeShippingCode
                ? <><span style={{ textDecoration: 'line-through', color: '#707070', marginRight: '6px' }}>${originalShipping.toFixed(2)}</span>FREE</>
                : `$${shipping.toFixed(2)}`
            }
          </span>
        </div>

        {/* Discount Code Row - Shopify style with label and code below */}
        {hasCodeDiscount && !isFreeShippingCode && (
          <div className="price-row discount-row">
            <div className="discount-row-label">
              <span className="discount-row-title">Order discount</span>
              <span className="discount-row-code">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="12" height="12" focusable="false" aria-hidden="true">
                  <g strokeLinejoin="round" clipPath="url(#discount_row_svg__a)">
                    <path strokeLinecap="round" d="M10.5 1.5H7.93a2.5 2.5 0 0 0-1.8.766L1.52 7.052a1.5 1.5 0 0 0 .02 2.101l3.48 3.48a1.25 1.25 0 0 0 1.75.016l5.116-4.926a2 2 0 0 0 .613-1.441V3.5a2 2 0 0 0-2-2" fill="none" stroke="currentColor"/>
                    <circle cx="9.5" cy="4.5" r="0.563" strokeLinecap="round" fill="none" stroke="currentColor"/>
                    <path d="M9.49 4.49h.02v.02h-.02z" fill="none" stroke="currentColor"/>
                  </g>
                  <defs>
                    <clipPath id="discount_row_svg__a">
                      <path fill="#fff" d="M0 0h14v14H0z"/>
                    </clipPath>
                  </defs>
                </svg>
                {cart.discount_code}
              </span>
            </div>
            <span className="discount-amount">- ${codeDiscount.toFixed(2)}</span>
          </div>
        )}

        {appliedTip > 0 && (
          <div className="price-row shipping">
            <span>Tip</span>
            <span>${appliedTip.toFixed(2)}</span>
          </div>
        )}

        <div className="price-row total">
          <span>Total</span>
          <span>
            <span className="total-currency">USD</span> ${total.toFixed(2)}
          </span>
        </div>

        {/* Total Savings Section - includes volume discount + code discount + shipping savings */}
        {(() => {
          const volumeSavings = cart.total_savings ? parseFloat(cart.total_savings) : 0;
          const totalSavings = volumeSavings + codeDiscount + shippingSavings;
          if (totalSavings > 0) {
            return (
              <div className="price-row total-savings">
                <span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" width="14" height="14" focusable="false" aria-hidden="true" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 3.25v2.844a2.5 2.5 0 0 1-.708 1.743L7.75 12.25m1-10.5H6.699a2 2 0 0 0-1.414.586L1.737 5.883a1.75 1.75 0 0 0 0 2.475l2.332 2.331a1.5 1.5 0 0 0 2.121 0l3.724-3.724a2 2 0 0 0 .586-1.414V3.5a1.75 1.75 0 0 0-1.75-1.75" fill="none" stroke="currentColor"/>
                    <circle cx="7.75" cy="4.5" r="0.563" strokeLinecap="round" strokeLinejoin="round" fill="none" stroke="currentColor"/>
                    <path strokeLinejoin="round" d="M7.74 4.49h.02v.02h-.02z" fill="none" stroke="currentColor"/>
                  </svg>
                  TOTAL SAVINGS
                </span>
                <span>${totalSavings.toFixed(2)}</span>
              </div>
            );
          }
          return null;
        })()}
      </div>
      {/* End of Collapsible Content */}
      </div>
    </div>
  );
};

export default OrderSummary;
